import { useEffect, useState } from 'react';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { sendChangeReport } from '../../../services/user.service';
import { useAuth } from '../../../hooks/use-auth.hook';
import { AxiosError } from 'axios';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import { object, string } from 'yup';
import { Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import InputForm from '../../commun/input-form';
import Button from '@mui/material/Button';
import { UserAccountDto } from '../../../services/dtos/user-account.dto';
import './reportChangeDialog.scss';

/**
 * Dialog de formulaire
 *
 * @param {boolean} props.open - Etat du dialog
 * @param {function} props.handleClose - Fonction de fermeture du dialog
 * @param {Object} props.userInfos - Informations de l'utilsateur
 */
export default function ReportChangeDialogForm(
    props: Readonly<{
        open: boolean;
        userInfos: UserAccountDto;
        mandateId?: string;
        handleClose: () => void;
        onSuccess?: () => void;
    }>
) {
    const showMessage = useSnackbars();
    const auth = useAuth();
    const [validate, setValidate] = useState(false);

    useEffect(() => {
        if (!props.open) {
            setValidate(false);
        }
    }, [props.open]);

    const handleCloseDialog = () => {
        setValidate(false);
        props.handleClose();
    };

    /**
     * Fonction d'envoi du formulaire
     *
     * @param {UserAccountDto} e - Infos renseignées
     */
    const handleSubmit = (e: UserAccountDto) => {
        const idUser = props.mandateId ? props.userInfos.extId : auth?.user?.id;
        if (idUser) {
            setValidate(true);
            sendChangeReport(idUser.toString(), e, props.mandateId)
                .then(data => {
                    showMessage.successAlert(data.status);
                    props.onSuccess?.();
                    handleCloseDialog();
                })
                .catch((error: Error | AxiosError) => {
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setValidate(false);
                });
        }
    };

    const charactersNumberPhone = 10;

    const buildAddressSummary = (data: UserAccountDto): string => {
        const street = [data.numero, data.complementNumero, data.rue].filter(Boolean).join(' ').trim();
        const complements = [data.complementRue, data.batiment].filter(Boolean).join(', ').trim();
        const city = [data.boitePostale, data.codePostal, data.ville, data.complementVille]
            .filter(Boolean)
            .join(' ')
            .trim();
        const country = (data.pays ?? '').trim();

        return [street, complements, city, country].filter(Boolean).join(', ');
    };

    /**
     * Represents a validation schema for a user's information.
     * @typedef {Object} ValidationSchema
     */
    const validationSchema = object({
        rue: string().trim().required('La rue ne peut être vide.'),
        ville: string().trim().required('La ville ne peut être vide.'),
        codePostal: string().trim().required('Le code postal ne peut être vide.'),
        pays: string().trim().required('Le pays ne peut être vide.'),
        email: string()
            .required('Le mail ne peut être vide.')
            .email("L'email n'est pas valide")
            .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, "L'email n'est pas valide"),
        secondaryEmail: string()
            .email("L'email secondaire n'est pas valide")
            .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, {
                message: "L'email secondaire n'est pas valide",
                excludeEmptyString: true,
            }),
        landphone: string()
            .matches(/^\d+$/, {
                message: 'Caractère non valide inséré.',
            })
            .length(charactersNumberPhone, "Merci d'entrer 10 caractères."),
        cellphone: string()
            .required('Le téléphone portable ne peut être vide.')
            .matches(/^\d+$/, {
                message: 'Caractère non valide inséré.',
            })
            .length(charactersNumberPhone, "Merci d'entrer 10 caractères."),
    });

    /**
     * Represents the initial values for a form.
     *
     * @typedef {Object} IniValues
     * @property {string} email - The email of the user, defaults to an empty string if not provided.
     * @property {string} landphone - The landline phone number of the user, defaults to an empty string if not provided.
     * @property {string} cellphone - The cellphone number of the user, defaults to an empty string if not provided.
     */
    const iniValues: UserAccountDto = {
        address: props.userInfos.address ?? '',
        numero: props.userInfos.numero ?? '',
        complementNumero: props.userInfos.complementNumero ?? '',
        rue: props.userInfos.rue ?? '',
        complementRue: props.userInfos.complementRue ?? '',
        batiment: props.userInfos.batiment ?? '',
        codePostal: props.userInfos.codePostal ?? '',
        ville: props.userInfos.ville ?? '',
        complementVille: props.userInfos.complementVille ?? '',
        boitePostale: props.userInfos.boitePostale ?? '',
        pays: props.userInfos.pays ?? 'France',
        email: props.userInfos.email ?? '',
        secondaryEmail: props.userInfos.secondaryEmail ?? '',
        birthDate: props.userInfos.birthDate ?? '',
        landphone: props.userInfos.landphone ?? '',
        cellphone: props.userInfos.cellphone ?? '',
    };

    const maxBirthDate = new Date().toISOString().split('T')[0];

    const submitForm = (values: UserAccountDto) => {
        handleSubmit({
            ...values,
            address: buildAddressSummary(values),
            currentEmail: props.userInfos.email ?? '',
            clientUrl: globalThis.location.origin,
        });
    };

    return (
        <Dialog
            open={props.open}
            onClose={handleCloseDialog}
            className="dialog-change-report"
            PaperProps={{ className: 'dialog-change-report__paper' }}
            maxWidth="md"
            fullWidth
        >
            <Formik
                initialValues={iniValues}
                onSubmit={submitForm}
                validationSchema={validationSchema}
                enableReinitialize
            >
                <Form>
                    <DialogContent className="dialog-change-report__content">
                        <div className="dialog-change-report__intro">
                            <Typography variant="h4">Signaler un changement</Typography>
                            <Typography variant="body1" className="dialog-change-report__subtitle">
                                Vérifiez les informations à mettre à jour avant l&apos;envoi de votre demande.
                            </Typography>
                            <Typography variant="body2" className="dialog-change-report__caption">
                                Les champs marqués d&apos;un * sont obligatoires.
                            </Typography>
                        </div>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <InputForm id="email" name="email" label="Adresse email" type="email" required />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputForm
                                    id="secondaryEmail"
                                    name="secondaryEmail"
                                    label="Adresse email secondaire"
                                    type="email"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputForm
                                    id="birthDate"
                                    name="birthDate"
                                    label="Date de naissance"
                                    type="date"
                                    maxDate={maxBirthDate}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputForm id="landphone" name="landphone" label="Téléphone fixe" type="tel" />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputForm
                                    id="cellphone"
                                    name="cellphone"
                                    label="Téléphone portable"
                                    type="tel"
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <InputForm id="numero" name="numero" label="Numéro" />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <InputForm
                                    id="complementNumero"
                                    name="complementNumero"
                                    label="Complément N° (BIS, TER, etc)"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputForm id="rue" name="rue" label="Rue" required />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputForm id="complementRue" name="complementRue" label="Complément de rue" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputForm id="batiment" name="batiment" label="Bât / Étage / Apt / Esc" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputForm id="ville" name="ville" label="Ville" required />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputForm id="complementVille" name="complementVille" label="Complément de ville" />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputForm id="codePostal" name="codePostal" label="Code Postal" required />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputForm id="boitePostale" name="boitePostale" label="Boite Postale" />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputForm id="pays" name="pays" label="Pays" required />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions className="dialog-change-report__actions">
                        <Button variant="outlined" onClick={handleCloseDialog} data-testid="close">
                            Retour
                        </Button>
                        <Button type="submit" variant="contained" disabled={validate}>
                            {validate ? 'Envoi...' : 'Envoyer'}
                        </Button>
                    </DialogActions>
                </Form>
            </Formik>
        </Dialog>
    );
}
